import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Eye, Save, ExternalLink, Store } from 'lucide-react';
import { VENDOR_TEMPLATE_SHORT, VENDOR_TEMPLATE_FULL } from '../lib/vendorProfileTemplate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function CardPreview({ businessName, shortBio, verification, isFoundingVendor }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5 hover:shadow-lg transition-shadow" data-testid="preview-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <Store className="h-4 w-4 text-primary shrink-0" />
          <div className="font-heading text-lg font-medium truncate" data-testid="preview-card-name">
            {businessName || 'Your Business Name'}
          </div>
        </div>
        <div className="flex gap-1 shrink-0">
          {isFoundingVendor && <Badge className="bg-amber-100 text-amber-800 text-[10px]">Founding</Badge>}
          {verification === 'verified' && <Badge className="bg-blue-100 text-blue-800 text-[10px]">Verified</Badge>}
          {verification === 'trusted' && <Badge className="bg-green-100 text-green-800 text-[10px]">Trusted</Badge>}
        </div>
      </div>
      <p className="text-sm text-muted-foreground line-clamp-3" data-testid="preview-card-bio">
        {shortBio || 'Your short bio will appear here.'}
      </p>
      <div className="mt-3 text-xs text-primary hover:underline cursor-pointer">Visit storefront →</div>
    </div>
  );
}

function StorefrontHeaderPreview({ businessName, fullAbout, country, verification, isFoundingVendor }) {
  const paragraphs = (fullAbout || '').split(/\n\s*\n/).filter(Boolean);
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="preview-storefront">
      <div className="bg-gradient-to-br from-[hsl(var(--navy))] to-[hsl(var(--navy-dark))] text-white p-6">
        <div className="flex items-center gap-2 text-[hsl(var(--gold))] text-xs uppercase tracking-wider">
          <Store className="h-3.5 w-3.5" />
          Vendor Storefront
        </div>
        <h2 className="text-2xl font-heading font-medium mt-2" data-testid="preview-storefront-name">
          {businessName || 'Your Business Name'}
        </h2>
        <div className="flex gap-2 mt-3 flex-wrap">
          {isFoundingVendor && <Badge className="bg-amber-500/90 text-amber-950 text-[10px]">Founding Vendor</Badge>}
          {verification === 'verified' && <Badge className="bg-blue-500/90 text-white text-[10px]">Verified</Badge>}
          {verification === 'trusted' && <Badge className="bg-green-500/90 text-white text-[10px]">Trusted</Badge>}
          {country && <Badge variant="outline" className="border-white/40 text-white text-[10px]">{country}</Badge>}
        </div>
      </div>
      <div className="p-6 space-y-3 max-h-96 overflow-y-auto">
        {paragraphs.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">Your About/Story will appear here.</p>
        ) : (
          paragraphs.map((p, i) => (
            <p key={i} className="text-sm text-foreground leading-relaxed whitespace-pre-line" data-testid={`preview-storefront-paragraph-${i}`}>
              {p}
            </p>
          ))
        )}
      </div>
    </div>
  );
}

export default function VendorProfilePreview() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [vendor, setVendor] = useState(null);
  const [businessName, setBusinessName] = useState('');
  const [shortBio, setShortBio] = useState('');
  const [fullAbout, setFullAbout] = useState('');
  const [tab, setTab] = useState('card'); // 'card' | 'storefront'

  useEffect(() => {
    const fetchVendor = async () => {
      try {
        const r = await axios.get(`${API}/vendors/me`);
        setVendor(r.data);
        setBusinessName(r.data.business_name || '');
        setShortBio(r.data.description || '');
        setFullAbout(r.data.full_about || '');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load your vendor profile');
      } finally {
        setLoading(false);
      }
    };
    fetchVendor();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/vendors/me/profile`, {
        business_name: businessName.trim() || undefined,
        description: shortBio.trim(),
        full_about: fullAbout.trim(),
      });
      setVendor(r.data);
      toast.success('Profile saved. Preview reflects what buyers now see.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="h-64 bg-muted rounded-lg animate-pulse" data-testid="profile-preview-loading" />;
  }

  const shortBioLen = shortBio.length;
  const fullAboutLen = fullAbout.length;
  const wordCountShort = shortBio.trim() ? shortBio.trim().split(/\s+/).length : 0;
  const wordCountFull = fullAbout.trim() ? fullAbout.trim().split(/\s+/).length : 0;

  return (
    <div className="space-y-6" data-testid="vendor-profile-preview-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-heading font-medium flex items-center gap-2">
            <Eye className="h-5 w-5 text-primary" />
            Profile Preview
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            See exactly what buyers see. Edit on the left, preview updates live on the right.
          </p>
        </div>
        {vendor?.store_slug && (
          <Link
            to={`/store/${vendor.store_slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-primary hover:underline flex items-center gap-1"
            data-testid="visit-live-storefront-link"
          >
            <ExternalLink className="h-4 w-4" />
            Open live storefront
          </Link>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* LEFT: Editor */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Business name</CardTitle>
            </CardHeader>
            <CardContent>
              <Input
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="e.g., Kingdom Modest Apparel"
                data-testid="profile-business-name-input"
                maxLength={120}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Short bio</CardTitle>
                  <CardDescription>Shown on your vendor card + product pages (~60 words).</CardDescription>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs h-7 text-primary hover:text-primary hover:bg-primary/10"
                  onClick={() => setShortBio(VENDOR_TEMPLATE_SHORT)}
                  data-testid="profile-use-short-template"
                >
                  Use template
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Textarea
                value={shortBio}
                onChange={(e) => setShortBio(e.target.value)}
                rows={5}
                placeholder="One warm paragraph that tells buyers who you are and why they should trust you."
                data-testid="profile-short-bio-textarea"
                maxLength={800}
              />
              <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                <span>{wordCountShort} words</span>
                <span className={shortBioLen > 720 ? 'text-amber-600' : ''}>{shortBioLen}/800</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Full About / Story</CardTitle>
                  <CardDescription>Shown on your storefront hero (~250 words).</CardDescription>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs h-7 text-primary hover:text-primary hover:bg-primary/10"
                  onClick={() => setFullAbout(VENDOR_TEMPLATE_FULL)}
                  data-testid="profile-use-full-template"
                >
                  Use template
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Textarea
                value={fullAbout}
                onChange={(e) => setFullAbout(e.target.value)}
                rows={14}
                placeholder="Your longer story: mission, origin, how you serve, kingdom impact, how to reach you."
                data-testid="profile-full-about-textarea"
                maxLength={4000}
              />
              <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                <span>{wordCountFull} words</span>
                <span className={fullAboutLen > 3600 ? 'text-amber-600' : ''}>{fullAboutLen}/4000</span>
              </div>
            </CardContent>
          </Card>

          <Button
            onClick={save}
            disabled={saving}
            size="lg"
            className="w-full"
            data-testid="profile-save-button"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save profile'}
          </Button>
        </div>

        {/* RIGHT: Sticky preview */}
        <div className="lg:sticky lg:top-6 space-y-3 h-fit">
          <div className="flex gap-2 border-b border-border">
            <button
              onClick={() => setTab('card')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'card' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
              data-testid="preview-tab-card"
            >
              Vendor card
            </button>
            <button
              onClick={() => setTab('storefront')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'storefront' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
              data-testid="preview-tab-storefront"
            >
              Storefront hero
            </button>
          </div>

          {tab === 'card' ? (
            <>
              <p className="text-xs text-muted-foreground">This is what buyers see on featured-vendor lists and product pages.</p>
              <CardPreview
                businessName={businessName}
                shortBio={shortBio}
                verification={vendor?.verification}
                isFoundingVendor={vendor?.is_founding_vendor}
              />
            </>
          ) : (
            <>
              <p className="text-xs text-muted-foreground">This is your storefront page ({vendor?.store_slug ? `/store/${vendor.store_slug}` : 'live URL after approval'}).</p>
              <StorefrontHeaderPreview
                businessName={businessName}
                fullAbout={fullAbout}
                country={vendor?.country}
                verification={vendor?.verification}
                isFoundingVendor={vendor?.is_founding_vendor}
              />
            </>
          )}

          <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900">
            <strong>Tip:</strong> If your bio looks cut off in the card preview, shorten it. The card shows only 3 lines of text — anything past that is invisible on mobile.
          </div>
        </div>
      </div>
    </div>
  );
}
